import { PromotionCodeDTO } from "@func-core/core/domain/view-models";
import { isSessionStorageAvailable } from "@func-core/core/utils/browser-features";
import { fetchApiJson } from "@utils/request";
import { rollbar, RollbarLogArgument } from "@utils/rollbar";

const PROMOTION_CODE_PATHS = {
  validatePromotionCode: "validate",
};

const PROMOTION_CODE_STORAGE_KEY = "promotion_code";

export type ValidatePromotionCodeRequest = {
  leadKbKey: string;
  countryIso: string;
  code: string;
};

export type ValidatePromotionCodeResponse = PromotionCodeDTO | null;

export type PromotionCodeAdapter = {
  validatePromotionCode: (
    requestBody: ValidatePromotionCodeRequest
  ) => Promise<ValidatePromotionCodeResponse>;
  savePromotionCode: (promotionCode: PromotionCodeDTO) => void;
  getPromotionCode: () => PromotionCodeDTO | null;
  clearPromotionCode: () => void;
};

function promotionCodeUrl(path: string): string {
  if (!process.env.GATSBY_API_BASE) {
    rollbar.error("Missing GATSBY_API_BASE environment variable");
    throw new Error("Missing GATSBY_API_BASE environment variable");
  }

  return `${process.env.GATSBY_API_BASE}/billing/promotion_codes/${path}`;
}

export const createPromotionCodeAdapter = (): PromotionCodeAdapter => {
  const hasSessionStorage = isSessionStorageAvailable();

  const validatePromotionCode = async (requestBody: ValidatePromotionCodeRequest) => {
    return await fetchApiJson<ValidatePromotionCodeResponse>(
      promotionCodeUrl(PROMOTION_CODE_PATHS.validatePromotionCode),
      {
        method: "POST",
        body: JSON.stringify({
          lead_kb_key: requestBody.leadKbKey,
          country_iso: requestBody.countryIso,
          code: requestBody.code,
        }),
      }
    );
  };

  const savePromotionCode = (promotionCode: PromotionCodeDTO): void => {
    if (!hasSessionStorage) return;

    try {
      sessionStorage.setItem(PROMOTION_CODE_STORAGE_KEY, JSON.stringify(promotionCode));
    } catch (error) {
      rollbar.error(
        "Failed to save promotion code to session storage",
        error as RollbarLogArgument
      );
    }
  };

  const getPromotionCode = (): PromotionCodeDTO | null => {
    if (!hasSessionStorage) return null;

    try {
      const storedPromotionCode = sessionStorage.getItem(PROMOTION_CODE_STORAGE_KEY);

      return storedPromotionCode ? (JSON.parse(storedPromotionCode) as PromotionCodeDTO) : null;
    } catch (error) {
      rollbar.error(
        "Failed to get promotion code from session storage",
        error as RollbarLogArgument
      );

      return null;
    }
  };

  const clearPromotionCode = (): void => {
    if (!hasSessionStorage) return;

    try {
      sessionStorage.removeItem(PROMOTION_CODE_STORAGE_KEY);
    } catch (error) {
      rollbar.error(
        "Failed to clear promotion code from session storage",
        error as RollbarLogArgument
      );
    }
  };

  return {
    validatePromotionCode,
    savePromotionCode,
    getPromotionCode,
    clearPromotionCode,
  };
};
